import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle, Ban, Calculator, CheckCircle2, Circle, Copy, ExternalLink, FileUp, Info, Loader2, Printer,
} from 'lucide-react';
import { apiErrorMessage, handlePlanLimitError } from '../lib/planLimit';
import {
  FieldInput, PageShell, Panel, ProgressBar, StatusBadge, primaryButton, secondaryButton, useApi, useTone,
} from '../components/demarches/ui';

const CHECK_STYLE = {
  block: { icon: Ban, cls: 'border-rose-300 bg-rose-50 text-rose-900' },
  warn: { icon: AlertTriangle, cls: 'border-amber-300 bg-amber-50 text-amber-900' },
  info: { icon: Info, cls: 'border-sky-200 bg-sky-50 text-sky-900' },
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

const DocumentRow = ({ doc, onAttach, onUpload, busy }) => {
  const tone = useTone();
  const input = useRef(null);
  return (
    <li className={`rounded-xl p-3 flex flex-wrap items-center gap-3 ${tone.soft}`} data-testid={`piece-${doc.type}`}>
      {doc.status === 'ok'
        ? <CheckCircle2 className="text-emerald-500 shrink-0" size={20} />
        : <Circle className={`${tone.muted} shrink-0`} size={20} />}
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm font-semibold">
          {doc.label} {doc.required ? '' : <span className={`font-normal ${tone.muted}`}>(utile)</span>}
        </p>
        <p className={`text-xs ${tone.muted}`}>
          {doc.status === 'ok' ? `Repris de ton coffre : ${doc.document.name}` : doc.why}
        </p>
      </div>
      {doc.status === 'in_vault' && (
        <button type="button" className={secondaryButton} disabled={busy} onClick={() => onAttach(doc.type, doc.available.id)}>
          Utiliser celui de mon coffre
        </button>
      )}
      {doc.status !== 'ok' && (
        <>
          <input ref={input} type="file" accept=".pdf,image/*" className="hidden"
            onChange={(e) => e.target.files[0] && onUpload(doc.type, e.target.files[0])} />
          <button type="button" className={doc.status === 'missing' ? primaryButton : secondaryButton} disabled={busy}
            onClick={() => input.current.click()}>
            <FileUp size={16} /> Ajouter
          </button>
        </>
      )}
    </li>
  );
};

export const DossierPage = () => {
  const { id } = useParams();
  const api = useApi();
  const tone = useTone();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState('');
  const [showAll, setShowAll] = useState(false);

  const apply = useCallback((d) => {
    setData(d);
    setValues(Object.fromEntries(d.evaluation.fields.map((f) => [f.key, f.value])));
    setReference(d.dossier.reference || '');
  }, []);

  const load = useCallback(() => api.get(`/dossiers/${id}`).then(apply), [api, id, apply]);

  useEffect(() => {
    load().catch(() => { toast.error('Dossier introuvable'); navigate('/dashboard/demarches'); });
  }, [load, navigate]);

  const run = async (fn, success) => {
    setBusy(true);
    try {
      const d = await fn();
      if (d?.dossier) apply(d); else await load();
      if (success) toast.success(success);
    } catch (e) {
      if (!handlePlanLimitError(e, navigate)) toast.error(apiErrorMessage(e, 'Action impossible pour le moment'));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <PageShell title="Mon dossier" back="/dashboard/demarches" backLabel="Mes démarches"><Loader2 className="animate-spin" /></PageShell>;

  const { dossier, procedure, evaluation, summary, fiche } = data;
  const missing = evaluation.fields.filter((f) => !f.filled);
  const shownFields = showAll ? evaluation.fields : missing;
  const stepIndex = procedure.steps.findIndex((s) => s.id === evaluation.current_step);
  const closed = ['accepte', 'refuse', 'abandonne'].includes(dossier.status);

  const saveFields = () => run(async () => {
    const changed = Object.fromEntries(Object.entries(values).filter(([k, v]) => v !== evaluation.fields.find((f) => f.key === k)?.value));
    await api.patch('/profil', { values: changed });
    return api.get(`/dossiers/${id}`);
  }, 'Informations enregistrées dans ton profil');

  const copy = (text) => navigator.clipboard?.writeText(String(text)).then(() => toast.success('Copié'));

  return (
    <PageShell title={procedure.title} subtitle={procedure.summary} back="/dashboard/demarches" backLabel="Mes démarches" testId="dossier-page">
      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <StatusBadge status={dossier.status} />
          <span className="text-sm font-semibold" data-testid="dossier-progress">Avancement : {summary.progress_percent} %</span>
        </div>
        <ProgressBar value={summary.progress_percent} className="mb-4" />
        <ol className="grid grid-cols-5 gap-2">
          {procedure.steps.map((s, i) => (
            <li key={s.id} className="text-center">
              <span className={`mx-auto mb-1 flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                i < stepIndex || closed ? 'bg-[#81d8d0] text-[#0F0F1A]' : i === stepIndex ? 'border-2 border-[#81d8d0]' : tone.soft}`}>
                {i + 1}
              </span>
              <span className={`block text-[11px] leading-4 ${i === stepIndex ? 'font-semibold' : tone.muted}`}>{s.title}</span>
            </li>
          ))}
        </ol>
        {!closed && <p className="mt-4 text-sm"><strong>Prochaine étape :</strong> {summary.next_action}</p>}
      </Panel>

      {evaluation.checks.length > 0 && (
        <div className="space-y-2 mb-5">
          {evaluation.checks.map((c) => {
            const { icon: Icon, cls } = CHECK_STYLE[c.level];
            return (
              <div key={c.text} className={`flex gap-2 rounded-xl border p-3 text-sm ${cls}`}>
                <Icon size={18} className="shrink-0 mt-0.5" /> <span>{c.text}</span>
              </div>
            );
          })}
        </div>
      )}

      <Panel
        title="Estimation"
        right={<button type="button" className={secondaryButton} disabled={busy}
          onClick={() => run(() => api.post(`/dossiers/${id}/estimation`), null)}>
          <Calculator size={16} /> {dossier.estimation ? 'Recalculer' : 'Estimer mon montant'}
        </button>}
        testId="estimation"
      >
        {dossier.estimation ? (
          <>
            <p className="text-3xl font-semibold mb-1">
              {dossier.estimation.montant_mensuel > 0 ? `≈ ${Math.round(dossier.estimation.montant_mensuel)} € / mois` : 'Pas de droit estimé avec ton profil actuel'}
            </p>
            <p className={`text-xs leading-5 ${tone.muted}`}>
              Estimation, pas un montant accordé : seule la CAF décide. {dossier.estimation.disclaimer}
            </p>
            <details className={`text-xs mt-2 ${tone.muted}`}>
              <summary className="cursor-pointer">Hypothèses du calcul</summary>
              <ul className="list-disc ml-5 mt-1">{dossier.estimation.hypotheses.map((h) => <li key={h}>{h}</li>)}</ul>
            </details>
          </>
        ) : (
          <p className={`text-sm ${tone.muted}`}>Calcul OpenFisca (moteur officiel de la législation) à partir de ton profil.</p>
        )}
      </Panel>

      {!closed && (
        <Panel
          title={missing.length ? `1. Il nous manque ${missing.length} information${missing.length > 1 ? 's' : ''}` : '1. Tes informations sont complètes'}
          right={<button type="button" className={`text-xs underline ${tone.muted}`} onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Voir seulement les manquantes' : 'Tout revoir'}
          </button>}
          testId="step-profil"
        >
          {shownFields.length > 0 ? (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                {shownFields.map((f) => (
                  <FieldInput key={f.key} field={f} value={values[f.key]} onChange={(v) => setValues((p) => ({ ...p, [f.key]: v }))} />
                ))}
              </div>
              <button type="button" className={`${primaryButton} mt-4`} disabled={busy} onClick={saveFields} data-testid="save-fields">
                Enregistrer
              </button>
              <p className={`text-xs mt-2 ${tone.muted}`}>Enregistré dans ton profil : tu ne le ressaisiras pas pour tes autres démarches.</p>
            </>
          ) : (
            <p className={`text-sm ${tone.muted}`}>Tout est repris de ton profil.</p>
          )}
        </Panel>
      )}

      {!closed && (
        <Panel title="2. Les pièces" testId="step-pieces">
          {data.reuse_consent !== true && evaluation.documents.some((d) => d.status === 'in_vault') && (
            <div className={`rounded-xl p-3 mb-3 text-sm flex flex-wrap items-center gap-3 ${tone.soft}`}>
              <span className="flex-1">Certaines pièces sont déjà dans ton coffre. Skipya peut les reprendre automatiquement dans toutes tes démarches.</span>
              <button type="button" className={secondaryButton} disabled={busy}
                onClick={() => run(async () => { await api.patch('/profil', { reuse_consent: true }); return api.get(`/dossiers/${id}`); }, 'Réutilisation activée')}>
                Accepter la réutilisation
              </button>
            </div>
          )}
          <ul className="space-y-2">
            {evaluation.documents.map((doc) => (
              <DocumentRow key={doc.type} doc={doc} busy={busy}
                onAttach={(type, docId) => run(() => api.post(`/dossiers/${id}/documents`, { type, document_id: docId }), 'Pièce ajoutée au dossier')}
                onUpload={(type, file) => run(async () => {
                  const r = await api.upload(file, type);
                  return api.post(`/dossiers/${id}/documents`, { type, document_id: r.document.id });
                }, 'Pièce ajoutée au coffre et au dossier')}
              />
            ))}
          </ul>
        </Panel>
      )}

      <Panel
        title="3. Ta fiche de préparation"
        right={<button type="button" className={`${secondaryButton} print:hidden`} onClick={() => window.print()}><Printer size={16} /> Imprimer</button>}
        testId="step-fiche"
      >
        <p className={`text-sm mb-4 ${tone.muted}`}>
          Les réponses que le formulaire de la CAF va te demander. Garde cette page ouverte à côté pendant ton dépôt.
        </p>
        {fiche.map((s) => (
          <div key={s.section} className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#81d8d0] mb-2">{s.section}</p>
            <dl className="divide-y divide-gray-200/40">
              {s.fields.map((f) => (
                <div key={f.key} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <dt className={tone.muted}>{f.label}</dt>
                  <dd className="flex items-center gap-2 font-medium text-right">
                    {f.filled ? String(f.display) : <span className="text-amber-600">à compléter</span>}
                    {f.filled && <button type="button" aria-label={`Copier ${f.label}`} className={tone.muted} onClick={() => copy(f.display)}><Copy size={14} /></button>}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
        {!closed && dossier.status === 'en_preparation' && (
          <button type="button" className={primaryButton} disabled={busy || missing.length > 0}
            onClick={() => run(() => api.post(`/dossiers/${id}/fiche`), 'Fiche relue')} data-testid="validate-fiche">
            {dossier.fiche_validee_at ? 'Fiche relue ✓' : "J'ai relu ma fiche"}
          </button>
        )}
      </Panel>

      {!closed && (
        <Panel title="4. Déposer ta demande sur le site officiel" testId="step-depot">
          <ol className="list-decimal ml-5 text-sm space-y-1 mb-4">
            <li>Connecte-toi à ton compte sur caf.fr (ou crée-le avec ton numéro de sécurité sociale).</li>
            <li>{procedure.steps.find((s) => s.id === 'depot')?.detail}</li>
            <li>Recopie les réponses de ta fiche et joins les pièces demandées.</li>
            <li>Note le numéro ou l'accusé de réception affiché à la fin, puis reviens ici.</li>
          </ol>
          <p className={`text-xs mb-4 ${tone.muted}`}>Durée : {procedure.duration}. Skipya ne dépose rien à ta place et ne te demande jamais ton mot de passe CAF.</p>
          <div className="flex flex-wrap gap-2 mb-4">
            <a href={procedure.official_url} target="_blank" rel="noreferrer" className={primaryButton} data-testid="official-link">
              Ouvrir caf.fr <ExternalLink size={16} />
            </a>
            <a href={procedure.simulator_url} target="_blank" rel="noreferrer" className={secondaryButton}>Simulateur officiel</a>
          </div>
          {dossier.status !== 'depose' && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex-1 min-w-[200px] text-sm">
                <span className="block mb-1.5 font-medium">Numéro de demande ou d'allocataire (facultatif)</span>
                <input className={`w-full rounded-xl border px-3 py-2.5 ${tone.input}`} value={reference} onChange={(e) => setReference(e.target.value)} />
              </label>
              <button type="button" className={primaryButton} disabled={busy}
                onClick={() => run(() => api.patch(`/dossiers/${id}`, { status: 'depose', reference }), 'Bravo ! Skipya suit ta demande')}
                data-testid="mark-deposited">
                J'ai déposé ma demande
              </button>
            </div>
          )}
        </Panel>
      )}

      <Panel title="5. Suivi" testId="step-suivi">
        {dossier.status === 'depose' && (
          <div className="mb-4">
            <p className="text-sm mb-3">Demande déposée le {formatDate(dossier.deposited_at)}{dossier.reference ? ` (réf. ${dossier.reference})` : ''}. Quelle est la réponse de la CAF ?</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={primaryButton} disabled={busy} onClick={() => run(() => api.patch(`/dossiers/${id}`, { status: 'accepte' }), 'Félicitations !')}>Accordée</button>
              <button type="button" className={secondaryButton} disabled={busy} onClick={() => run(() => api.patch(`/dossiers/${id}`, { status: 'refuse' }), null)}>Refusée</button>
            </div>
          </div>
        )}
        {dossier.status === 'refuse' && (
          <p className="text-sm mb-4">Un refus peut se contester : la CAF indique dans sa notification comment demander un recours amiable. Le copilote peut t'aider à rédiger ta demande.</p>
        )}
        {data.reminders.length > 0 && (
          <>
            <p className="text-sm font-semibold mb-2">Rappels programmés (par e-mail)</p>
            <ul className="space-y-1 mb-4">
              {data.reminders.filter((r) => !r.completed).map((r) => (
                <li key={r.id} className="text-sm">• {formatDate(r.due_date)} : {r.title}</li>
              ))}
            </ul>
          </>
        )}
        <p className="text-sm font-semibold mb-2">Historique</p>
        <ul className={`text-sm space-y-1 ${tone.muted}`}>
          {dossier.history.map((h) => <li key={h.at}>{formatDate(h.at)} : <StatusBadge status={h.status} /></li>)}
        </ul>
        {!closed && (
          <button type="button" className={`text-xs underline mt-4 ${tone.muted}`} disabled={busy}
            onClick={() => window.confirm('Abandonner ce dossier ?') && run(() => api.patch(`/dossiers/${id}`, { status: 'abandonne' }), null)}>
            Abandonner ce dossier
          </button>
        )}
      </Panel>
    </PageShell>
  );
};

export default DossierPage;
